import { collection, doc } from 'firebase/firestore';
import { ref, uploadBytes } from 'firebase/storage';
import { STORAGE_URL, firestore, storage } from 'services';
import { StoryChapter, StoryChapterMediaFieldsType } from 'types';
import { FileState, StoryChapterFields, StoryFields, StoryFieldsType } from 'validations/story';
import { PUZZLE_IMAGE } from 'app-constants';
import { ImageFormat } from 'services/utility/image/resizeImage';
import { getVideoMetadata } from 'services/media';

import { getImageResolution } from './getImageResolution';
import { resizeImage } from './resizeImage';

const ValidFileState = ['added', 'exists'];
const VideoPuzzleKeys = [
  'iconAnimatedUrl',
  'iconUrl',
  'original',
  'previewPlaceholderUrl',
  'previewVideoUrl',
] as StoryChapterMediaFieldsType[];

export const prepareChaptersForUpdate = async (
  storyId: string,
  chapters: StoryFieldsType[StoryFields.Chapters],
  files: { [key: string]: File },
) => {
  const errors = chapters
    .map((chapter, index) => {
      if (chapter.type === 'static') {
        if (!chapter?.original?.state || !ValidFileState.includes(chapter?.original?.state))
          return [`Chapter ${index + 1} is missing an image!`];
      }
      if (chapter.type === 'video') {
        const chapterErrors: (string | null)[] = VideoPuzzleKeys.map((videoPuzzleKey) => {
          const typedKey = videoPuzzleKey as keyof typeof chapter;
          const fileState = chapter?.[typedKey] as FileState;
          if (!fileState?.state || !ValidFileState.includes(fileState?.state))
            return `Chapter ${index + 1} is missing an ${videoPuzzleKey}!`;

          return null;
        }).filter((errorValue) => errorValue !== null);

        return chapterErrors as string[];
      }
      return [];
    })
    .flat();

  if (errors.length > 0) {
    return {
      errors: errors,
    };
  }

  const preparedChapters = await Promise.all(
    chapters.map(async (chapter, index) => {
      const hasId = !(chapter?.[StoryChapterFields.Id].split('-')[0] === 'virtual');
      const chapterDocRef = !hasId
        ? doc(collection(firestore, `stories/${storyId}/chapters`))
        : doc(firestore, `stories/${storyId}/chapters/${chapter?.[StoryChapterFields.Id]}`);

      const preparedChapterData: Partial<StoryChapter> = {
        chapterNumber: index + 1,
        difficulty: String(chapter.difficulty.value),
        chapterIntroduction: chapter.chapterIntroduction,
        type: chapter.type,
        _meta: {},
      };

      const baseKey = `${StoryFields.Chapters}.${chapter?.[StoryChapterFields.Id]}`;
      const originalKey = `${baseKey}.original`;
      const originalFile = files?.[originalKey];
      const originalUrl = chapter?.original?.url;
      const originalRef = ref(storage, `stories/${storyId}/chapters/${chapterDocRef.id}/original`);

      if (originalFile && originalUrl) {
        if (chapter.type === 'static') {
          const originalResolution = await getImageResolution(originalUrl);

          const iconRef = ref(storage, `stories/${storyId}/chapters/${chapterDocRef.id}/icon`);
          const urlRef = ref(storage, `stories/${storyId}/chapters/${chapterDocRef.id}/image`);
          const lowQualityRef = ref(
            storage,
            `stories/${storyId}/chapters/${chapterDocRef.id}/lowQualityImage`,
          );
          const highQualityRef = ref(
            storage,
            `stories/${storyId}/chapters/${chapterDocRef.id}/highQualityImage`,
          );

          const [icon, regular, low, high, original] = await Promise.all([
            // Icon file
            (async () => {
              const resizeResult = await resizeImage(
                originalUrl,
                PUZZLE_IMAGE.DIGITAL.CROP_ICON.WIDTH,
                PUZZLE_IMAGE.DIGITAL.CROP_ICON.HEIGHT,
              );
              const uploadResult = await uploadBytes(iconRef, resizeResult, {
                contentType: ImageFormat.JPEG,
              });

              return uploadResult;
            })(),
            // Regular Version
            (async () => {
              const resizeResult = await resizeImage(
                originalUrl,
                PUZZLE_IMAGE.DIGITAL.CROP.WIDTH,
                PUZZLE_IMAGE.DIGITAL.CROP.HEIGHT,
              );
              const uploadResult = await uploadBytes(urlRef, resizeResult, {
                contentType: ImageFormat.JPEG,
              });

              return uploadResult;
            })(),
            // Low Quality Version
            (async () => {
              const resizeResult = await resizeImage(
                originalUrl,
                PUZZLE_IMAGE.DIGITAL.CROP_LQ.WIDTH,
                PUZZLE_IMAGE.DIGITAL.CROP_LQ.HEIGHT,
              );
              const uploadResult = await uploadBytes(lowQualityRef, resizeResult, {
                contentType: ImageFormat.JPEG,
              });

              return uploadResult;
            })(),
            // High Quality Version
            (async () => {
              if (
                !(
                  originalResolution.width >= PUZZLE_IMAGE.DIGITAL.CROP_HQ.WIDTH &&
                  originalResolution.height >= PUZZLE_IMAGE.DIGITAL.CROP_HQ.HEIGHT
                )
              )
                return undefined;

              const resizeResult = await resizeImage(
                originalUrl,
                PUZZLE_IMAGE.DIGITAL.CROP_HQ.WIDTH,
                PUZZLE_IMAGE.DIGITAL.CROP_HQ.HEIGHT,
              );
              const uploadResult = await uploadBytes(highQualityRef, resizeResult, {
                contentType: ImageFormat.JPEG,
              });

              return uploadResult;
            })(),

            // Original
            (async () => {
              const uploadResult = await uploadBytes(
                originalRef,
                await originalFile.arrayBuffer(),
                { contentType: originalFile.type },
              );

              return uploadResult;
            })(),
          ]);

          preparedChapterData._meta = {};

          if (icon) {
            preparedChapterData.iconUrl = `${STORAGE_URL}/${
              icon.ref.fullPath
            }?timestamp=${new Date().getTime()}`;
          }
          if (regular) {
            preparedChapterData.url = `${STORAGE_URL}/${
              regular.ref.fullPath
            }?timestamp=${new Date().getTime()}`;
            preparedChapterData._meta.resolution = `${PUZZLE_IMAGE.DIGITAL.CROP.WIDTH}x${PUZZLE_IMAGE.DIGITAL.CROP.HEIGHT}`;
          }
          if (low) {
            preparedChapterData.lowQualityImageUrl = `${STORAGE_URL}/${
              low.ref.fullPath
            }?timestamp=${new Date().getTime()}`;
            preparedChapterData._meta.lowQualityResolution = `${PUZZLE_IMAGE.DIGITAL.CROP_LQ.WIDTH}x${PUZZLE_IMAGE.DIGITAL.CROP_LQ.HEIGHT}`;
          }
          if (high) {
            preparedChapterData.highQualityImageUrl = `${STORAGE_URL}/${
              high.ref.fullPath
            }?timestamp=${new Date().getTime()}`;
            preparedChapterData._meta.highQualityResolution = `${PUZZLE_IMAGE.DIGITAL.CROP_HQ.WIDTH}x${PUZZLE_IMAGE.DIGITAL.CROP_HQ.HEIGHT}`;
          }
          if (original) {
            preparedChapterData.originalUrl = `${STORAGE_URL}/${
              original.ref.fullPath
            }?timestamp=${new Date().getTime()}`;
            preparedChapterData._meta.originalResolution = `${originalResolution.width}x${originalResolution.height}`;
          }
        }
      }

      if (chapter.type === 'video') {
        const originalResolution = originalUrl ? await getVideoMetadata(originalUrl) : undefined;
        const videoResolution = chapter?.videoUrl?.url
          ? await getVideoMetadata(chapter?.videoUrl?.url)
          : undefined;

        const iconRef = ref(storage, `stories/${storyId}/chapters/${chapterDocRef.id}/icon`);
        const urlRef = ref(storage, `stories/${storyId}/chapters/${chapterDocRef.id}/video`);
        const iconAnimatedRef = ref(
          storage,
          `stories/${storyId}/chapters/${chapterDocRef.id}/iconAnimated`,
        );
        const previewPlaceholderRef = ref(
          storage,
          `stories/${storyId}/chapters/${chapterDocRef.id}/previewPlaceholder`,
        );
        const previewVideoRef = ref(
          storage,
          `stories/${storyId}/chapters/${chapterDocRef.id}/previewVideo`,
        );

        const [icon, url, animatedIcon, previewPlaceholder, previewVideo, original] =
          await Promise.all([
            // Icon
            (async () => {
              const iconFile = files?.[`${baseKey}.iconUrl`];
              if (!iconFile) return undefined;

              const uploadResult = await uploadBytes(iconRef, await iconFile.arrayBuffer(), {
                contentType: iconFile.type,
              });

              return uploadResult;
            })(),
            // Video
            (async () => {
              const videoFile = files?.[`${baseKey}.videoUrl`];
              if (!videoFile) return undefined;

              const uploadResult = await uploadBytes(urlRef, await videoFile.arrayBuffer(), {
                contentType: videoFile.type,
              });

              return uploadResult;
            })(),
            // Animated icon
            (async () => {
              const animateIconFile = files?.[`${baseKey}.iconAnimatedUrl`];
              if (!animateIconFile) return undefined;

              const uploadResult = await uploadBytes(
                iconAnimatedRef,
                await animateIconFile.arrayBuffer(),
                { contentType: animateIconFile.type },
              );

              return uploadResult;
            })(),
            // Preview Placeholder
            (async () => {
              const previewPlaceholderFile = files?.[`${baseKey}.previewPlaceholderUrl`];
              if (!previewPlaceholderFile) return undefined;

              const uploadResult = await uploadBytes(
                previewPlaceholderRef,
                await previewPlaceholderFile.arrayBuffer(),
                { contentType: previewPlaceholderFile.type },
              );

              return uploadResult;
            })(),
            // Preview Video
            (async () => {
              const previewVideoFile = files?.[`${baseKey}.previewVideoUrl`];
              if (!previewVideoFile) return undefined;

              const uploadResult = await uploadBytes(
                previewVideoRef,
                await previewVideoFile.arrayBuffer(),
                { contentType: previewVideoFile.type },
              );

              return uploadResult;
            })(),
            // Original
            (async () => {
              if (!originalFile) return undefined;
              const uploadResult = await uploadBytes(
                originalRef,
                await originalFile.arrayBuffer(),
                { contentType: originalFile.type },
              );

              return uploadResult;
            })(),
          ]);

        preparedChapterData._meta = {};

        if (icon) {
          preparedChapterData.iconUrl = `${STORAGE_URL}/${
            icon.ref.fullPath
          }?timestamp=${new Date().getTime()}`;
        }
        if (url) {
          preparedChapterData.url = `${STORAGE_URL}/${
            url.ref.fullPath
          }?timestamp=${new Date().getTime()}`;
          if (videoResolution) {
            preparedChapterData._meta.resolution = `${videoResolution.width}x${videoResolution.height}`;
          }
        }
        if (animatedIcon) {
          preparedChapterData.videoIconUrl = `${STORAGE_URL}/${
            animatedIcon.ref.fullPath
          }?timestamp=${new Date().getTime()}`;
        }
        if (previewPlaceholder) {
          preparedChapterData.staticPreviewPlaceholderUrl = `${STORAGE_URL}/${
            previewPlaceholder.ref.fullPath
          }?timestamp=${new Date().getTime()}`;
        }
        if (previewVideo) {
          preparedChapterData.videoPreviewUrl = `${STORAGE_URL}/${
            previewVideo.ref.fullPath
          }?timestamp=${new Date().getTime()}`;
        }
        if (original) {
          preparedChapterData.originalUrl = `${STORAGE_URL}/${
            original.ref.fullPath
          }?timestamp=${new Date().getTime()}`;

          if (originalResolution) {
            preparedChapterData._meta.originalResolution = `${originalResolution.width}x${originalResolution.height}`;
          }
        }
      }

      return {
        data: preparedChapterData,
        docRef: chapterDocRef,
      };
    }),
  );

  return { preparedChapters };
};
